import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicOrganisation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateOrganisation } from "../api/update-organisation";
import {
  OrganisationContactDetailsFormData,
  organisationContactDetailsSchema,
} from "../types/organisation-contact-details-schema";

type OrganisationContactDetailsFormProps = {
  initialData: PublicOrganisation;
};

function apiDataToFormData(
  data: PublicOrganisation,
): OrganisationContactDetailsFormData {
  return {
    website: data.website ?? "",
    phoneNumber: data.phoneNumber ?? "",
  };
}

export function OrganisationContactDetailsForm(
  props: OrganisationContactDetailsFormProps,
) {
  const { initialData } = props;
  const form = useForm<OrganisationContactDetailsFormData>({
    resolver: zodResolver(organisationContactDetailsSchema),
    defaultValues: apiDataToFormData(initialData),
  });
  const { mutateAsync: updateOrganisation } = useUpdateOrganisation({
    onSuccess: (result) => {
      form.reset(apiDataToFormData(result));
      queryClient.setQueryData(["organisation", result.id], result);
      queryClient.setQueryData(
        ["organisations"],
        (oldData: PublicOrganisation[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((org) => (org.id === result.id ? result : org));
        },
      );
    },
  });

  async function handleUpdateOrganisation(
    data: OrganisationContactDetailsFormData,
  ) {
    try {
      await updateOrganisation({ id: initialData.id, organisation: data });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateOrganisation)}>
          <Card.Header title="Contact Details" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website URL</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="07123 456789" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset(apiDataToFormData(initialData))}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
